import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  type SelectChangeEvent,
} from "@mui/material";
import { Alert, Button, Card, Col, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { fetchAllTracksForCourses } from "../utils/trackUtils";
import { fetchCoursesByTrack, type Course } from "../utils/courseUtils";
import { fetchBatchesByCourseSingle, type Batch } from "../utils/batchUtils";
import { AttendancePageShell } from "./utils/AttendancePageShell";
import {
  computeGroupSplit,
  describeGroupSplitLimitError,
  formatCurrentDate,
  getCurrentTime,
  MAX_GROUPS,
  MAX_STUDENTS_PER_GROUP,
  MIN_GROUPS,
  EVENT_TYPES,
} from "./data/attendanceConstants";
import type { AttendanceConfig, TrainerLiveState } from "./types/attendance";
import {
  createAttendanceSession,
  fetchAttendanceConfig,
  toSessionDatetime,
} from "./services/attendanceApi";

interface TrackOption {
  track_id: string;
  track_name: string;
}

function attendanceApiErrorDetail(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  if (typeof detail !== "string" || !detail.trim()) return fallback;
  if (detail.includes("Event name already exists")) {
    return "Event name already exists. Choose a different name.";
  }
  return detail;
}

const AttendanceSetup: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<AttendanceConfig | null>(null);
  const [tracks, setTracks] = useState<TrackOption[]>([]);
  const [selectedTrackId, setSelectedTrackId] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
  const [eventType] = useState(EVENT_TYPES[0]);
  const [eventName, setEventName] = useState("");
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [splitError, setSplitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const isPageLoading =
    loadingConfig || loadingTracks || loadingCourses || loadingBatches;

  const maxGroups = config?.maxGroups ?? MAX_GROUPS;
  const maxStudentsPerGroup = config?.maxStudentsPerGroup ?? MAX_STUDENTS_PER_GROUP;
  const minGroups = config?.minGroups ?? MIN_GROUPS;
  const activeImageCount =
    config?.activeImageCount ?? config?.images?.length ?? 0;

  const trimmedEventName = eventName.trim();
  const canTriggerAttendance =
    Boolean(selectedCourseId) &&
    selectedBatchIds.length > 0 &&
    trimmedEventName.length > 0 &&
    !isPageLoading;

  useEffect(() => {
    setLoadingConfig(true);
    void fetchAttendanceConfig()
      .then(setConfig)
      .catch((error) => {
        console.error("Failed to load attendance config:", error);
      })
      .finally(() => setLoadingConfig(false));
  }, []);

  useEffect(() => {
    const loadTracks = async () => {
      setLoadingTracks(true);
      try {
        const data = await fetchAllTracksForCourses();
        setTracks(data);
        if (data.length > 0) {
          setSelectedTrackId(data[0].track_id);
        }
      } catch (error) {
        console.error("Error fetching tracks:", error);
      } finally {
        setLoadingTracks(false);
      }
    };
    void loadTracks();
  }, []);

  useEffect(() => {
    if (!selectedTrackId) return;
    const loadCourses = async () => {
      setLoadingCourses(true);
      try {
        const data = await fetchCoursesByTrack(selectedTrackId);
        setCourses(data);
        setSelectedCourseId((prev) => {
          if (data.length === 0) return "";
          if (prev && data.some((c) => c.course_id === prev)) return prev;
          return data[0].course_id;
        });
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoadingCourses(false);
      }
    };
    void loadCourses();
  }, [selectedTrackId]);

  useEffect(() => {
    if (!selectedCourseId) return;
    const loadBatches = async () => {
      setLoadingBatches(true);
      try {
        const data = await fetchBatchesByCourseSingle(selectedCourseId);
        setBatches(data);
        setSelectedBatchIds((prev) =>
          prev.filter((id) => data.some((batch) => batch.batch_id === id)),
        );
      } catch (error) {
        console.error("Error fetching batches:", error);
      } finally {
        setLoadingBatches(false);
      }
    };
    void loadBatches();
  }, [selectedCourseId]);

  const selectedCourse = courses.find((c) => c.course_id === selectedCourseId);
  const selectedBatchItems = batches.filter((b) => selectedBatchIds.includes(b.batch_id));

  const totalStudents = useMemo(() => {
    if (selectedBatchItems.length === 0) return 0;
    return selectedBatchItems.reduce(
      (sum, batch) => sum + (batch.students_count || 0),
      0,
    );
  }, [selectedBatchItems]);

  const groupSizes = useMemo(
    () =>
      totalStudents > 0
        ? computeGroupSplit(
            totalStudents,
            maxStudentsPerGroup,
            minGroups,
            maxGroups,
            activeImageCount,
          )
        : [],
    [totalStudents, maxStudentsPerGroup, minGroups, maxGroups, activeImageCount],
  );

  const handleOpenGroupModal = () => {
    setSplitError("");

    if (!config) {
      setSplitError("Loading attendance configuration. Please try again in a moment.");
      setShowGroupModal(true);
      return;
    }

    if (!trimmedEventName) {
      setSplitError("Please enter an event name.");
      setShowGroupModal(true);
      return;
    }

    if (selectedBatchIds.length === 0) {
      setSplitError("Please select at least one batch.");
      setShowGroupModal(true);
      return;
    }

    if (totalStudents <= 0) {
      setSplitError("No students found for the selected batch(es).");
      setShowGroupModal(true);
      return;
    }

    if (activeImageCount < (config.studentChoiceCount ?? 3)) {
      setSplitError(
        `At least ${config.studentChoiceCount ?? 3} active attendance pattern images are required (found ${activeImageCount}).`,
      );
      setShowGroupModal(true);
      return;
    }

    const limitError = describeGroupSplitLimitError(
      totalStudents,
      maxStudentsPerGroup,
      maxGroups,
      activeImageCount,
    );
    if (limitError) {
      setSplitError(limitError);
      setShowGroupModal(true);
      return;
    }

    setShowGroupModal(true);
  };

  const handleGenerateGroups = async () => {
    if (splitError || groupSizes.length === 0 || !trimmedEventName) return;

    const courseLabel = selectedCourse?.course_name ?? "Course";
    const batchLabels = selectedBatchItems.map((b) => b.batch_name);

    setSubmitting(true);
    try {
      const session = await createAttendanceSession({
        setup_mode: "others",
        course_id: selectedCourseId,
        batch_ids: selectedBatchIds,
        purpose_detail: trimmedEventName.slice(0, 200),
        event_type: eventType,
        session_datetime: toSessionDatetime(formatCurrentDate(), getCurrentTime()),
        total_students: totalStudents,
        group_count: groupSizes.length,
      });

      const liveState: TrainerLiveState = {
        setupMode: "others",
        course: courseLabel,
        courseId: selectedCourseId,
        batches: batchLabels,
        batchIds: selectedBatchIds,
        purposeDetail: session.purpose_detail || trimmedEventName,
        eventType,
        dateTime: `${formatCurrentDate()} ${getCurrentTime()}`,
        totalStudents: session.total_students,
        groupSizes: session.group_sizes,
        groupCount: session.group_count,
        attendanceSessionId: session.attendance_session_id,
      };

      navigate("/admin/attendance/live", { state: liveState });
    } catch (error) {
      console.error("Error creating attendance session:", error);
      setSplitError(
        attendanceApiErrorDetail(
          error,
          "Failed to create attendance session. Please try again.",
        ),
      );
      setShowGroupModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const batchOptions = batches.map((b) => ({ value: b.batch_id, label: b.batch_name }));

  return (
    <>
      <AttendancePageShell style={{ position: "relative" }}>
        <div
          style={{
            filter: isPageLoading ? "blur(2px)" : undefined,
            pointerEvents: isPageLoading ? "none" : undefined,
            userSelect: isPageLoading ? "none" : undefined,
            transition: "filter 0.15s ease",
          }}
          aria-busy={isPageLoading}
        >
          <Card className="border-0 shadow-sm rounded-3 p-0">
            <Card.Body className="p-4">
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <FormControl fullWidth size="small" disabled={isPageLoading}>
                    <InputLabel id="attendance-setup-track-label" shrink>
                      Track
                    </InputLabel>
                    <Select
                      labelId="attendance-setup-track-label"
                      label="Track"
                      notched
                      displayEmpty
                      value={selectedTrackId}
                      onChange={(e) => setSelectedTrackId(String(e.target.value))}
                      renderValue={(value) => {
                        if (!value) return "Select track";
                        return (
                          tracks.find((t) => t.track_id === value)?.track_name ?? String(value)
                        );
                      }}
                    >
                      <MenuItem value="">Select track</MenuItem>
                      {tracks.map((t) => (
                        <MenuItem key={t.track_id} value={t.track_id}>
                          {t.track_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
                <Col md={6}>
                  <FormControl fullWidth size="small" disabled={isPageLoading}>
                    <InputLabel id="attendance-setup-course-label" shrink>
                      Course
                    </InputLabel>
                    <Select
                      labelId="attendance-setup-course-label"
                      label="Course"
                      notched
                      displayEmpty
                      value={selectedCourseId}
                      onChange={(e) => setSelectedCourseId(String(e.target.value))}
                      renderValue={(value) => {
                        if (!value) return "Select course";
                        return (
                          courses.find((c) => c.course_id === value)?.course_name ?? String(value)
                        );
                      }}
                    >
                      <MenuItem value="">Select course</MenuItem>
                      {courses.map((c) => (
                        <MenuItem key={c.course_id} value={c.course_id}>
                          {c.course_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <FormControl fullWidth size="small" disabled={isPageLoading}>
                    <InputLabel id="attendance-setup-batch-label" shrink>
                      Batch
                    </InputLabel>
                    <Select<string[]>
                      labelId="attendance-setup-batch-label"
                      label="Batch"
                      multiple
                      notched
                      displayEmpty
                      value={selectedBatchIds}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const next = e.target.value;
                        setSelectedBatchIds(typeof next === "string" ? next.split(",") : next);
                      }}
                      renderValue={(selected) => {
                        if (selected.length === 0) return "Select batches";
                        const labels = batchOptions
                          .filter((opt) => selected.includes(opt.value))
                          .map((opt) => opt.label);
                        if (labels.length <= 2) return labels.join(", ");
                        return `${labels.length} selected`;
                      }}
                    >
                      {batchOptions.length === 0 && (
                        <MenuItem disabled value="">
                          No batches
                        </MenuItem>
                      )}
                      {batchOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox size="small" checked={selectedBatchIds.includes(opt.value)} />
                          <ListItemText primary={opt.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
                <Col md={6}>
                  <FormControl fullWidth size="small" disabled>
                    <InputLabel id="attendance-setup-event-type-label">Event type</InputLabel>
                    <Select
                      labelId="attendance-setup-event-type-label"
                      label="Event type"
                      value={eventType}
                    >
                      {EVENT_TYPES.map((et) => (
                        <MenuItem key={et} value={et}>
                          {et}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Event name"
                    placeholder="Enter a unique event name"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    disabled={isPageLoading}
                    inputProps={{ maxLength: 200 }}
                  />
                </Col>
              </Row>

              <div className="d-flex flex-wrap gap-2 mt-4">
                <Button
                  variant="primary"
                  onClick={handleOpenGroupModal}
                  disabled={!canTriggerAttendance}
                >
                  Trigger Attendance
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate("/admin/attendance/report")}
                  disabled={isPageLoading}
                >
                  View Report
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>

        {isPageLoading && (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              backgroundColor: "rgba(255, 255, 255, 0.35)",
              backdropFilter: "blur(1px)",
            }}
            role="status"
            aria-live="polite"
            aria-label="Loading"
          >
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3 mb-0 small">Loading...</p>
          </div>
        )}
      </AttendancePageShell>

      <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Automatic Group Split</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {splitError ? (
            <Alert variant="danger" className="mb-0">
              {splitError}
            </Alert>
          ) : (
            <>
              <p className="mb-3">
                <strong>{totalStudents}</strong> students will be split into{" "}
                <strong>{groupSizes.length}</strong> group
                {groupSizes.length !== 1 ? "s" : ""} (max of {maxStudentsPerGroup}{" "}
                students per group):
              </p>
              <div className="rounded-3 border overflow-hidden">
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-primary">
                    <tr>
                      <th className="ps-3 py-2 fw-semibold">Group</th>
                      <th className="pe-3 py-2 fw-semibold text-end">Students</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupSizes.map((size, i) => (
                      <tr key={i}>
                        <td className="ps-3 py-2">Group {i + 1}</td>
                        <td className="pe-3 py-2 text-end fw-semibold">{size}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <td className="ps-3 py-2 fw-semibold">Total</td>
                      <td className="pe-3 py-2 text-end fw-bold text-primary">
                        {totalStudents}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowGroupModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleGenerateGroups()}
            disabled={!!splitError || groupSizes.length === 0 || submitting}
          >
            {submitting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Creating Session...
              </>
            ) : (
              "Generate Groups"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AttendanceSetup;
